/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import classnames from 'classnames';

import { IVinhusetFullscreenContentBlockData } from './vinhuset-fullscreen-content-block.data';
import { IVinhusetFullscreenContentBlockProps } from './vinhuset-fullscreen-content-block.props.autogenerated';

import LinksComponent from './components/links';

export interface IVinhusetFullscreenContentBlockViewProps extends IVinhusetFullscreenContentBlockProps<IVinhusetFullscreenContentBlockData> {
    title?: React.ReactNode;
    text?: React.ReactNode;
    links?: React.ReactNode;
    backgroundImage?: React.ReactNode;
    contentBlockContainer: IModuleProps;
    detailsContainer: INodeProps;
    backgroundImageContainer: INodeProps;
}

/**
 *
 * VinhusetFullscreenContentBlock component
 * @extends {React.PureComponent<IVinhusetFullscreenContentBlockProps<IVinhusetFullscreenContentBlockData>>}
 */
class VinhusetFullscreenContentBlock extends React.PureComponent<IVinhusetFullscreenContentBlockProps<IVinhusetFullscreenContentBlockData>> {
    private readonly telemetryContent: ITelemetryContent = getTelemetryObject(
        this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry
    );

    public render(): JSX.Element | null {
        const { className, heading, image, links, paragraph } = this.props.config;

        const title = heading && (
            <Msdyn365.Text
                className='vinhuset-fullscreen-content-block__title'
                tag={heading.tag ?? 'h1'}
                text={heading.text}
                editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }} />
        );
        const text = paragraph && (
            <Msdyn365.RichTextComponent
                text={paragraph}
                className='vinhuset-fullscreen-content-block__text'
                editProps={{ onEdit: this.handleParagraphChange, requestContext: this.props.context.request }}
            />
        );
        const contentBlockLinks = links && ArrayExtensions.hasElements(links) && (
            <LinksComponent
                {...{
                    links,
                    onTextChange: this.handleLinkTextChange,
                    requestContext: this.props.context.request,
                    telemetryContent: this.telemetryContent
                }}
            />
        );
        const imageProps = {
            gridSettings: this.props.context.request.gridSettings ?? {},
            imageSettings: image?.imageSettings
        };

        const backgroundImage = image && (
            <Msdyn365.Image
                {...image}
                {...imageProps}
                src={`${image.src}&f=webp`}
                requestContext={this.props.context.request}
                preserveImageSpace={true}
                editProps={{
                    key: this.props.config.image ?? {},
                    requestContext: this.props.context.request,
                    moduleType: this.props.typeName,
                    imagePropertyName: 'image',
                    moduleId: this.props.id
                }}
                shouldSkipToMainImage
                title={undefined} /* We don't want tooltip on background image */
            />
        );

        const contentBlockViewProps = {
            ...this.props,
            title,
            text,
            links: contentBlockLinks,
            backgroundImage,
            contentBlockContainer: {
                moduleProps: this.props,
                className: classnames('vinhuset-fullscreen-content-block', className)
            },
            detailsContainer: { className: 'vinhuset-fullscreen-content-block__details' },
            backgroundImageContainer: { className: 'vinhuset-fullscreen-content-block__background-image-container' }
        } as IVinhusetFullscreenContentBlockViewProps;

        return this.props.renderView(contentBlockViewProps);
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading!.text = event.target.value);

    public handleParagraphChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.paragraph = event.target.value);

    public handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent) => {
        if (this.props.config.links && this.props.config.links[Number(linkIndex)]) {
            this.props.config.links[Number(linkIndex)].linkText = event.target.value;
        }
    };
}

export default VinhusetFullscreenContentBlock;
